import React from "react";
import {
    Card,
    CardContent,
    Typography,
    TextField,
    MenuItem,
    Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFurnitureStore } from "../../../contexts/SelectedVariantContext";
import SedutaFinishesSelector from "./SedutaFinishesSelector";
import { finishesById } from "../../../utils/colors";
import { variants } from "./furnitureVariants";

const wpcFinishIds = ["wpc_wood", "wpc_dark_grey", "wpc_white"];
const pannelliWpcFinishIds = ["wpc_wood", "wpc_copper_brown"];
const metalloFinishIds = ["ral_9006", "ral_custom"];
const pergodominoPlasticaFinishIds = [
    "pl_bianco",
    "pl_grigio_perla",
    "pl_grigio_platino",
    "pl_antracite",
    "pl_tortora",
];
const dominoLightsFinishIds = ["al_black", "al_silver"];

export default function ComplementoFinishesSelector() {
    const { t } = useTranslation();
    const { selectedVariant, setSelectedVariant } = useFurnitureStore();
    if (!selectedVariant) return null;

    const { category, material: rawMaterial } = selectedVariant;
    const material = rawMaterial.toLowerCase();

    if (category === "seduta") {
        return <SedutaFinishesSelector />;
    }

    // Special handling for pannello_frangivista - material variant selection
    if (selectedVariant?.subCategory === "pannello_frangivista") {
        const size = selectedVariant.id.match(/p_frangivista_(\d+)_/)?.[1];
        if (!size) return null;

        const materialVariants = variants.filter(v =>
            v.id.includes(`p_frangivista_${size}_`)
        );

        const handleMaterialChange = (variantId) => {
            const newVariant = materialVariants.find(v => v.id === variantId);
            if (newVariant) {
                setSelectedVariant({ ...newVariant, colorId: selectedVariant.colorId });
            }
        };

        return (
            <Card sx={{ my: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t("accessories_settings.furnishing_select.select_material", "Seleziona materiale")}
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        value={selectedVariant.id}
                        onChange={(e) => handleMaterialChange(e.target.value)}
                        label={t("accessories_settings.furnishing_select.select_material", "Seleziona materiale")}
                    >
                        {materialVariants.map((variant) => (
                            <MenuItem key={variant.id} value={variant.id}>
                                {t(`accessories_settings.furnishing_select.material_${variant.material}`, variant.material)}
                            </MenuItem>
                        ))}
                    </TextField>
                </CardContent>
            </Card>
        );
    }

    let finishIds;
    if (category === "pannelli_grigliati" && material === "wpc") {
        finishIds = pannelliWpcFinishIds;
    } else if (material === "wpc") {
        finishIds = wpcFinishIds;
    } else if (category === "pergodomino" && material === "plastica") {
        finishIds = pergodominoPlasticaFinishIds;
    } else if (category === "domino_lights" && material === "alluminio") {
        finishIds = dominoLightsFinishIds;
    } else if (material === "metallo") {
        finishIds = category === "exte" || category === "cover"
            ? metalloFinishIds
            : ["ral_custom"];
    } else if (material === "acciaio_inox") {
        finishIds = ["acciaio_inox_default"];
    } else {
        return null;
    }

    const handleFinishChange = (id) =>
        setSelectedVariant({ ...selectedVariant, colorId: id });

    return (
        <Card sx={{ my: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t("settings.drawer.finishes.select", "Seleziona finitura/colore")}
                </Typography>
                <TextField
                    select
                    fullWidth
                    value={selectedVariant.colorId || ""}
                    onChange={(e) => handleFinishChange(e.target.value)}
                    label={t("settings.drawer.finishes.select", "Seleziona finitura/colore")}
                >
                    {finishIds.map((id) => {
                        const { name, imageUrl } = finishesById[id];
                        return (
                            <MenuItem key={id} value={id}>
                                {imageUrl ? (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img
                                            src={imageUrl}
                                            alt={name}
                                            style={{ width: 40, height: 40, marginRight: 8 }}
                                        />
                                        <span>{name}</span>
                                    </Box>
                                ) : (
                                    name
                                )}
                            </MenuItem>
                        );
                    })}
                </TextField>
            </CardContent>
        </Card>
    );
}
